import type { ConnectionState, RealtimeHandler, RealtimeMessage, Unsubscribe } from './types'

/** Shared bookkeeping for state listeners and topic handlers. */
export class TopicRegistry {
  private readonly handlers = new Map<string, Set<RealtimeHandler>>()
  private readonly stateListeners = new Set<(state: ConnectionState) => void>()

  topics(): string[] {
    return [...this.handlers.keys()]
  }

  /** Returns true when this is the first handler for the topic. */
  add(topic: string, handler: RealtimeHandler): boolean {
    let set = this.handlers.get(topic)
    const isFirst = set === undefined
    if (!set) {
      set = new Set()
      this.handlers.set(topic, set)
    }
    set.add(handler)
    return isFirst
  }

  /** Returns true when the last handler for the topic was removed. */
  remove(topic: string, handler: RealtimeHandler): boolean {
    const set = this.handlers.get(topic)
    if (!set) return false
    set.delete(handler)
    if (set.size > 0) return false
    this.handlers.delete(topic)
    return true
  }

  dispatch(message: RealtimeMessage): void {
    const set = this.handlers.get(message.topic)
    if (!set) return
    for (const handler of [...set]) handler(message)
  }

  onState(listener: (state: ConnectionState) => void): Unsubscribe {
    this.stateListeners.add(listener)
    return () => this.stateListeners.delete(listener)
  }

  emitState(state: ConnectionState): void {
    for (const listener of [...this.stateListeners]) listener(state)
  }
}
